import { useMemo, useCallback, useState, useEffect } from 'react';
import { uniqBy } from 'lodash';
import {
  Box,
  Stack,
  Button,
  Tooltip,
  FormControlLabel,
  Switch,
  Drawer,
  Divider,
  IconButton,
  Typography,
  Grid,
  Tabs,
  Tab,
  Alert,
  Select,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { useForm, useFormContext } from 'react-hook-form';
import Iconify from '../../../components/iconify';
import Scrollbar from '../../../components/scrollbar';
import FormProvider, {
  RHFSelect,
  RHFUploadServiceImage,
  RHFTextField,
  RHFSwitch,
  RHFCategorySelect,
} from '../../../components/hook-form';
import InheritableFieldGroup from '../../../components/inheritable-field';
import axios from '../../../utils/Axios';
import WorkingHours from '../../../components/working-hours';
import { formatDuration } from '../../../utils/formatTime';
import { getValidConfig } from '../../../utils/schedule';
import saveErrorMessage from '../../../utils/saveErrorMessage';
import { useAuthContext } from 'src/auth/useAuthContext';
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';
import UnsavedChangesDialog from '../../../components/unsaved-changes-dialog';

const BUFFER_OPTIONS = [
  { label: 'No Buffer', value: 0 },
  { label: '5 Mins', value: 5 },
  { label: '10 Mins', value: 10 },
  { label: '15 Mins', value: 15 },
  { label: '20 Mins', value: 20 },
  { label: '30 Mins', value: 30 },
  { label: '45 Mins', value: 45 },
  { label: '60 Mins', value: 60 },
];

const PRESET_SLOT_INCREMENTS = [5, 10, 15, 20, 30, 45, 60, 90, 120];

const SLOT_INCREMENT_OPTIONS = [
  { label: '5 mins', value: 5 },
  { label: '10 mins', value: 10 },
  { label: '15 mins', value: 15 },
  { label: '20 mins', value: 20 },
  { label: '30 mins', value: 30 },
  { label: '45 mins', value: 45 },
  { label: '60 mins', value: 60 },
  { label: '90 mins', value: 90 },
  { label: '120 mins', value: 120 },
  { label: 'Custom…', value: 'custom' },
];

const StyledLabel = styled('span')(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
  fontSize: 14,
  lineHeight: 2.5,
}));

let currentImage = '';

function hasActiveAvailability(availability = []) {
  return availability.some((day) => day.startDate && day.endDate && day.startDate !== 'off' && day.endDate !== 'off');
}

function getServiceBufferMode(serviceConfig) {
  if (serviceConfig?.bufferMode) return serviceConfig.bufferMode;
  if (!serviceConfig) return 'GLOBAL';

  const beforeBuffer = Number(serviceConfig.beforeBuffer || 0);
  const afterBuffer = Number(serviceConfig.afterBuffer || 0);
  return hasActiveAvailability(serviceConfig.availability) || beforeBuffer > 0 || afterBuffer > 0
    ? 'SERVICE'
    : 'GLOBAL';
}

export default function ServicesModal({
  openModal,
  onCloseModal,
  service,
  categories,
  createService,
  updateService,
  isEdit,
  onOpenDialog,
  onOpenCategoriesModal,
  onCategoryCreated,
  setSuccess, // Added new prop for handling success messages
}) {
  const { user } = useAuthContext();

  const defaultValues = useMemo(
    () => {
      // Find the "Uncategorized" category if it exists in the database
      const uncategorizedCategory = categories.find(cat => 
        cat.name.toLowerCase() === 'uncategorized'
      );
      
      // Default category logic
      let defaultCategory = '';
      if (service?.category?._id) {
        defaultCategory = service.category._id;
      } else if (uncategorizedCategory) {
        defaultCategory = uncategorizedCategory._id;
      } else {
        defaultCategory = 'uncategorized'; // fallback to hardcoded option
      }

      return {
        name: service?.name || '',
        previewImage: service?.previewImage || '',
        duration: service?.duration || 30,
        durationType: service?.durationType || 'Minutes',
        durationTypeValue: service?.durationTypeValue || 30,
        category: defaultCategory,
        config: service
          ? {
            ...getValidConfig(service.config, {
              shouldIncludeTimezone: false,
              shouldIncludeStatus: false,
            }),
            bufferMode: getServiceBufferMode(service.config),
          }
          : {
            beforeBuffer: 0,
            afterBuffer: 0,
            bufferMode: 'GLOBAL',
          },
        hidden: service?.hidden ? service?.hidden : false,
        description: service?.description ? service?.description : '',
        bufferDays: service?.bufferDays ? service?.bufferDays : "0",
        capacity: service?.capacity ? service?.capacity : 1,
        price: service?.price ? service?.price : 0,
        slotIncrement: service?.slotIncrement || 30,
      };
    },
    [service, categories]
  );

  const methods = useForm({ defaultValues });
  const [tab, setTab] = useState(0);
  const [image, setImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [availability, setAvailability] = useState([]);
  const [formErrors, setFormErrors] = useState(null);
  const [unsavedChangesDialog, setUnsavedChangesDialog] = useState({ open: false, data: null });
  const [isCustomSlotIncrement, setIsCustomSlotIncrement] = useState(
    () => !PRESET_SLOT_INCREMENTS.includes(defaultValues.slotIncrement)
  );

  const {
    reset,
    handleSubmit,
    setValue,
    setError,
    watch,
    getValues,
    formState: { isSubmitting, isDirty, isSubmitSuccessful },
  } = methods;
  const bufferMode = watch('config.bufferMode') || defaultValues.config?.bufferMode || 'GLOBAL';

  // User-level globals are the "inherited" values shown when this service
  // is in GLOBAL mode. Default to 0 so brand-new users without explicit
  // global buffers see a clear "No buffer (global)" label.
  const userBeforeBuffer = Number(user?.config?.beforeBuffer ?? 0);
  const userAfterBuffer = Number(user?.config?.afterBuffer ?? 0);

  const overrideForService = () => {
    // Prefill both selects with the user's current globals so the user
    // tweaks from a sensible starting point. shouldDirty stays false on the
    // values themselves; only the mode flip should mark the form dirty.
    setValue('config.beforeBuffer', userBeforeBuffer, { shouldDirty: false });
    setValue('config.afterBuffer', userAfterBuffer, { shouldDirty: false });
    setValue('config.bufferMode', 'SERVICE', { shouldDirty: true });
  };

  const resetToGlobal = () => {
    // Flip mode only — the inherited display reads the user's globals
    // directly via props in BufferSubField. Backend ignores
    // beforeBuffer/afterBuffer when bufferMode is GLOBAL.
    setValue('config.bufferMode', 'GLOBAL', { shouldDirty: true });
  };

  // Unsaved changes hook. onSubmit catches a refused save, so react-hook-form still counts
  // it as a successful submit. The shown error marks the edits as not saved.
  const { handleClose } = useUnsavedChanges({
    formState: { isDirty, isSubmitSuccessful: isSubmitSuccessful && !formErrors },
    onUnsavedChanges: (data) => {
      setUnsavedChangesDialog({ open: true, data });
    },
    isOpen: openModal
  });

  // The page can close the drawer itself, for example after a delete.
  useEffect(() => {
    if (!openModal) setFormErrors(null);
  }, [openModal]);

  useEffect(() => {
    reset(defaultValues);
    if (defaultValues && defaultValues.config.availability) {
      setAvailability(defaultValues.config.availability);
    }
    setIsCustomSlotIncrement(!PRESET_SLOT_INCREMENTS.includes(defaultValues.slotIncrement));
    return () => {
      currentImage = '';
    };
  }, [isEdit, service, reset, defaultValues]);

  const onSubmit = async (data) => {
    if (formErrors) {
      setFormErrors(null);
    }

    if (data.durationType === 'Minutes' && (data.durationTypeValue < 0 || data.durationTypeValue > 60)) {
      setFormErrors({ message: 'Duration must be between 0 and 60 minutes for minutes type' });
      return;
    }

    if (data.durationType === 'Hours' && (data.durationTypeValue < 0 || data.durationTypeValue > 24)) {
      setFormErrors({ message: 'Duration must be between 0 and 24 hours for hours type' });
      return;
    }

    const inc = Number(data.slotIncrement);
    if (!Number.isInteger(inc) || inc < 5 || inc > 1440) {
      setError('slotIncrement', {
        type: 'manual',
        message: 'Slot increment must be a whole number between 5 and 1440 minutes.',
      });
      return;
    }
    data.slotIncrement = inc;

    data.config = data.config || {};
    data.config.bufferMode = data.config.bufferMode || 'GLOBAL';
    data.config.availability = availability.filter(
      (day) => day.startDate !== 'off' && day.endDate !== 'off'
    );

    // Handle uncategorized services
    if (data.category === 'uncategorized') {
      data.category = null; // Send null to backend for uncategorized services
    }

    try {
      setIsLoading(true);

      if (image && !currentImage) {
        const response = await axios.post('/upload', { path: 'services' });
        const signData = response.data;

        const formData = new FormData();
        formData.append('file', image);

        const url = `https://api.cloudinary.com/v1_1/de7fg4j42/image/upload?api_key=437499416445219&timestamp=${signData.timestamp}&signature=${signData.signature}&discard_original_filename=true&folder=${signData.folder}`;

        const imageResponse = await fetch(url, {
          method: 'POST',
          body: formData,
        });

        const imageJson = await imageResponse.json();
        if (imageJson.url) {
          currentImage = imageJson.url;
        } else {
          throw new Error("Couldn't upload the preview image try again later.");
        }
      }

      if (isEdit) {
        await updateService({
          ...data,
          previewImage: !currentImage ? data.previewImage : currentImage,
        });
      } else {
        await createService({
          ...data,
          previewImage: !currentImage ? data.previewImage : currentImage,
        });
      }

      // Close modal directly after successful save without unsaved changes check
      onCloseModal();
      setFormErrors(null);
      setTab(0);
      setAvailability([]);
      setUnsavedChangesDialog({ open: false, data: null });
      reset();
    } catch (error) {
      setFormErrors({ message: saveErrorMessage(error, 'An error occurred. Please try again.') });
      
      // Show error in the main page if setSuccess is available
      if (setSuccess) {
        setSuccess('Failed to save service. Please try again.', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });

      if (file) {
        currentImage = null;
        setValue('previewImage', newFile, { shouldValidate: true });
        setImage(file);
      }
    },
    [setValue]
  );

  const handleTabChange = (_, newTab) => {
    setTab(newTab);
  };

  const handleCloseModal = () => {
    handleClose(() => {
      onCloseModal();
      setFormErrors(null);
      setTab(0);
      setAvailability([]);
      setUnsavedChangesDialog({ open: false, data: null });
    });
  };

  // Handle unsaved changes dialog actions
  const handleUnsavedChangesDiscard = () => {
    if (unsavedChangesDialog.data?.onDiscard) {
      unsavedChangesDialog.data.onDiscard();
    }
    setUnsavedChangesDialog({ open: false, data: null });
  };

  const handleUnsavedChangesCancel = () => {
    setUnsavedChangesDialog({ open: false, data: null });
  };

  const handleOpenCategories = () => {
    if (onOpenCategoriesModal) {
      handleCloseModal();
      onOpenCategoriesModal();
    }
  };

  const hasAvilability = availability.some((time) => time.startDate !== 'off');

  // Add function to handle inline category creation
  const handleInlineCreateCategory = async (categoryName) => {
    try {
      const response = await axios.post('/categories', {
        name: categoryName,
      });
      
      // Update categories list with new category
      const newCategory = response.data.category;
      if (onCategoryCreated) {
        onCategoryCreated(newCategory);
      }
      
      return newCategory;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create category');
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={openModal}
        onClose={handleCloseModal}
        PaperProps={{
          sx: {
            width: {
              xs: 1,
              sm: 480,
            },
          },
        }}
      >
      {!!formErrors && <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>{formErrors.message}</Alert>}
      <ToolBar>
        <FormControlLabel
          labelPlacement="start"
          control={
            <Switch
              onChange={(_, val) =>
                setValue('hidden', !val, { shouldValidate: true })
              }
              defaultChecked={!defaultValues.hidden}
            />
          }
          label="Status"
        />
        <Stack direction="row" justifyContent="flex-end" flexGrow={1}>
          {isEdit && (
            <Tooltip title="Delete">
              <IconButton size="small" onClick={onOpenDialog}>
                <Iconify icon="eva:trash-2-outline" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Close">
            <IconButton onClick={handleCloseModal}>
              <Iconify icon="eva:close-fill" />
            </IconButton>
          </Tooltip>
        </Stack>
      </ToolBar>
      <Divider />
      <Stack sx={{ p: 2 }}>
        <Typography variant="h5">Service</Typography>
      </Stack>

      <Scrollbar>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={handleTabChange} variant="fullWidth">
            <Tab label="Details" />
            <Tab label="Working Hours" />
          </Tabs>
        </Box>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <TabPanel value={tab} index={0}>
            <Stack sx={{ p: 2 }}>
              <Grid
                container
                mb={3}
                sx={{ alignItems: 'center', justifyContent: 'center' }}
              >
                <Grid item xs={12} md={3}>
                  <RHFUploadServiceImage
                    multiple
                    thumbnail
                    name="previewImage"
                    maxSize={3145728}
                    onDrop={handleDrop}
                    onUpload={() => console.log('ON UPLOAD')}
                    sx={{ height: 90, width: 90 }}
                  />
                </Grid>
                <Grid item xs={12} md={9}>
                  <StyledLabel>Name:</StyledLabel>
                  <RHFTextField
                    name="name"
                    size="small"
                    InputProps={{
                      sx: { typography: 'body2' },
                    }}
                  />
                </Grid>
              </Grid>
              <Box
                rowGap={3}
                columnGap={2}
                display="grid"
                gridTemplateColumns={{
                  xs: 'repeat(1, 1fr)',
                  sm: 'repeat(2, 1fr)',
                }}
              >
                <Stack spacing={0}>
                  <StyledLabel>Category</StyledLabel>
                  <RHFCategorySelect
                    name="category"
                    categories={categories}
                    onCreateCategory={handleInlineCreateCategory}
                    size="small"
                  />
                </Stack>

                <Stack>
                  <StyledLabel>Duration</StyledLabel>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{ marginLeft: 5, width: 90 }}>
                      <RHFTextField name="durationTypeValue" size="small" type="number" />
                    </div>
                    <div style={{ marginLeft: 5, width: 110 }}>
                      <RHFSelect native name="durationType" size="small">
                        <option value="Minutes">Minutes</option>
                        <option value="Hours">Hours</option>
                      </RHFSelect>
                    </div>
                  </div>
                </Stack>

                {/*
                  Buffer inputs use the Webflow-style inheritance UX:
                  one card groups Before/After under a shared mode. Inherited =
                  faded disabled selects showing the user's global values; the
                  only affordance is the header "Customize" link. Customized =
                  standard editable selects; header swaps to a quieter "Reset
                  to global" link. Mode is shared via config.bufferMode.
                */}
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <InheritableFieldGroup
                    title="Buffer Time"
                    isInherited={bufferMode === 'GLOBAL'}
                    onCustomize={overrideForService}
                    onReset={resetToGlobal}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 1.5,
                      }}
                    >
                      <BufferSubField
                        subLabel="Before"
                        name="config.beforeBuffer"
                        isInherited={bufferMode === 'GLOBAL'}
                        inheritedValue={userBeforeBuffer}
                      />
                      <BufferSubField
                        subLabel="After"
                        name="config.afterBuffer"
                        isInherited={bufferMode === 'GLOBAL'}
                        inheritedValue={userAfterBuffer}
                      />
                    </Box>
                  </InheritableFieldGroup>
                </Box>

                {user.config.enablePayments ? <Stack>
                  <StyledLabel>Price</StyledLabel>
                  <RHFTextField name="price" size="small" type="number" />
                </Stack> : null}

                <Stack>
                  <StyledLabel>Maximum Capacity</StyledLabel>
                  <RHFTextField name="capacity" size="small" type="number" />
                </Stack>

                <Stack>
                  <StyledLabel>Customers can't schedule within</StyledLabel>
                  <div className='flex items-center' style={{ position: 'relative' }}>
                    <RHFTextField
                      name="bufferDays"
                      size="small"
                      InputProps={{
                        sx: { typography: 'body2' },
                      }}
                    />
                    <div style={{ position: 'absolute', top: '1px', right: '1px', background: '#ededed', padding: '5px 10px', borderTopRightRadius: '7px', borderBottomRightRadius: '7px' }}>
                      days
                    </div>
                  </div>
                </Stack>

                <Stack>
                  <StyledLabel>
                    Slot Increment
                    <Tooltip
                      title={
                        <span>
                          How far apart each available start time is shown to customers.<br /><br />
                          <b>Example:</b> A 60-min service with 15-min increment shows slots at 9:00, 9:15, 9:30... Customers have more start times to choose from, but overlapping slots are automatically blocked once a booking is made (based on capacity).<br /><br />
                          Set increment equal to duration for back-to-back scheduling with no gaps or overlaps.<br /><br />
                          Custom values must be whole minutes between 5 and 1440 (24h). Increments larger than your daily availability window simply produce fewer slots per day &mdash; bookings remain valid.
                        </span>
                      }
                      placement="top"
                      arrow
                    >
                      <IconButton size="small" sx={{ ml: 0.5, p: 0 }}>
                        <Iconify icon="eva:info-outline" width={18} />
                      </IconButton>
                    </Tooltip>
                  </StyledLabel>
                  <Select
                    native
                    fullWidth
                    size="small"
                    value={isCustomSlotIncrement ? 'custom' : Number(watch('slotIncrement'))}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === 'custom') {
                        setIsCustomSlotIncrement(true);
                        const current = Number(getValues('slotIncrement'));
                        if (PRESET_SLOT_INCREMENTS.includes(current)) {
                          setValue('slotIncrement', 30, { shouldDirty: true });
                        }
                      } else {
                        setIsCustomSlotIncrement(false);
                        setValue('slotIncrement', Number(v), { shouldDirty: true });
                      }
                    }}
                  >
                    {SLOT_INCREMENT_OPTIONS.map((option, index) => (
                      <option key={index} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                  {isCustomSlotIncrement && (
                    <Box sx={{ position: 'relative', mt: 1 }}>
                      <RHFTextField
                        name="slotIncrement"
                        size="small"
                        type="number"
                        inputProps={{ min: 5, max: 1440, step: 1 }}
                      />
                      <Box sx={{ position: 'absolute', top: '1px', right: '1px', bottom: '1px', background: '#ededed', padding: '5px 10px', borderTopRightRadius: '7px', borderBottomRightRadius: '7px' }}>
                        minutes
                      </Box>
                    </Box>
                  )}
                </Stack>
              </Box>
              <Stack sx={{ mt: 3, mb: 3 }}>
                <StyledLabel>Description:</StyledLabel>
                <RHFTextField
                  multiline
                  name="description"
                  rows={4}
                  size="small"
                />
              </Stack>
            </Stack>
          </TabPanel>
          <TabPanel value={tab} index={1}>
            <Box sx={{ p: 2 }}>
              {!hasAvilability && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <Typography>
                    Service working hours are being set from the global working
                    hours, change one the days below and save to use custom
                    service hours.
                  </Typography>
                </Alert>
              )}
              {tab && (
                <WorkingHours
                  availability={availability}
                  setAvailability={setAvailability}
                />
              )}
            </Box>
          </TabPanel>
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            flexGrow={1}
            mr={2}
            mb={2}
          >
            <Button
              onClick={handleCloseModal}
              size="medium"
              color="inherit"
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="medium"
              variant="contained"
              disabled={isLoading || isSubmitting}
              sx={{
                '&:disabled': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  opacity: 0.7
                }
              }}
            >
              {isLoading || isSubmitting ? (
                <>
                  <Box 
                    component="span" 
                    sx={{ 
                      display: 'inline-block',
                      width: 20,
                      height: 20,
                      mr: 1,
                      borderRadius: '50%',
                      borderTop: '2px solid currentColor',
                      borderRight: '2px solid transparent',
                      animation: 'spin 0.75s linear infinite',
                      '@keyframes spin': {
                        '0%': {
                          transform: 'rotate(0deg)',
                        },
                        '100%': {
                          transform: 'rotate(360deg)',
                        },
                      },
                    }} 
                  />
                  Processing...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </Stack>
        </FormProvider>
      </Scrollbar>
    </Drawer>

    {/* Unsaved Changes Dialog */}
    <UnsavedChangesDialog
      open={unsavedChangesDialog.open}
      onClose={handleUnsavedChangesCancel}
      onDiscard={handleUnsavedChangesDiscard}
      isSaving={isLoading || isSubmitting}
      title="Unsaved Service Changes"
      message="You have unsaved changes to this service. What would you like to do?"
    />
  </>
  );
}

function BufferSubField({ subLabel, name, isInherited, inheritedValue }) {
  const {
    formState: { dirtyFields },
  } = useFormContext();

  // Resolve dotted path (e.g. "config.beforeBuffer") against dirtyFields.
  const isFieldDirty = name
    .split('.')
    .reduce((acc, key) => (acc ? acc[key] : undefined), dirtyFields);

  // When inherited, the display is bound directly to the user's global
  // (passed as `inheritedValue`) — NOT to the RHF form value. This keeps
  // the readout correct regardless of what the service's own beforeBuffer
  // happens to hold, and avoids syncing two sources of truth.
  const inheritedIsPreset = BUFFER_OPTIONS.some((b) => b.value === inheritedValue);

  const label = (
    <Typography
      variant="caption"
      sx={{
        fontSize: 11,
        color: isInherited ? 'text.disabled' : 'text.secondary',
        fontWeight: isInherited ? 400 : 500,
        lineHeight: 1.4,
        transition: (theme) =>
          theme.transitions.create('color', { duration: 120 }),
      }}
    >
      {subLabel}
    </Typography>
  );

  if (isInherited) {
    return (
      <Stack spacing={0.5}>
        {label}
        <Select
          native
          disabled
          size="small"
          value={inheritedValue}
          sx={{
            transition: (theme) =>
              theme.transitions.create(['background-color', 'border-color'], {
                duration: 120,
              }),
            bgcolor: 'grey.200',
            '& .MuiSelect-select': { color: 'text.secondary' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
          }}
        >
          {BUFFER_OPTIONS.map((buffer) => (
            <option key={buffer.value} value={buffer.value}>
              {buffer.label}
            </option>
          ))}
          {!inheritedIsPreset && (
            <option value={inheritedValue}>{`${inheritedValue} mins`}</option>
          )}
        </Select>
      </Stack>
    );
  }

  return (
    <Stack spacing={0.5}>
      {label}
      <RHFSelect
        native
        name={name}
        size="small"
        sx={{
          '& .MuiOutlinedInput-root': {
            transition: (theme) =>
              theme.transitions.create(['box-shadow'], { duration: 120 }),
            ...(isFieldDirty && {
              boxShadow: (theme) =>
                `0 0 0 3px ${alpha(theme.palette.primary.main, 0.24)}`,
            }),
          },
        }}
      >
        {BUFFER_OPTIONS.map((buffer) => (
          <option key={buffer.value} value={buffer.value}>
            {buffer.label}
          </option>
        ))}
      </RHFSelect>
    </Stack>
  );
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      <Stack sx={{ mt: 2 }}>{children}</Stack>
    </div>
  );
}

function ToolBar({ children }) {
  return (
    <Stack pt={3} pb={3} direction="row" alignItems="center">
      <Stack
        direction="row"
        spacing={1}
        justifyContent="flex-end"
        flexGrow={1}
        pr={2}
      >
        {children}
      </Stack>
    </Stack>
  );
}
